import React, { FC, useState, useEffect } from 'react';
import _ from 'lodash';
import { Box, Popover, Text, TextButton } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone/i18n';
import { AppName, TemplateGalleryCard } from '@common/types';
import { sortAppsByPriority } from '@services/TemplateListingUtils';
import { useShallowRouter } from '@components/Hooks/useRouter';
import { QueryParams, updateQueryParams } from '@utils/route';

interface IFeatureTagsProps {
  isMobileView?: boolean;
  features?: TemplateGalleryCard['apps'];
  disableFiltering?: boolean;
}

const FIRST_FEATURES_SIZE = 2;

const FeatureTags = ({
  features,
  isMobileView,
  disableFiltering,
}: IFeatureTagsProps) => {
  const { t } = useTranslation();
  const [firstFeatures, setFirstFeatures] = useState<AppName[]>();
  const [moreFeatures, setMoreFeatures] = useState<AppName[]>();
  const [shown, setShown] = useState<boolean>(false);
  const filteredFeatures = _.filter(
    features,
    (feature) => feature.isKey !== false,
  );
  const featuresCount = filteredFeatures?.length || 0;

  useEffect(() => {
    if (features) {
      const sortedFeatures = sortAppsByPriority(filteredFeatures);

      setFirstFeatures(_.slice(sortedFeatures, 0, FIRST_FEATURES_SIZE));
      setMoreFeatures(_.slice(sortedFeatures, FIRST_FEATURES_SIZE));
    }
  }, [features, t]);

  return (
    <Box gap="6px" paddingTop="14px" height="22px">
      <>
        {firstFeatures &&
          _.map(firstFeatures, (firstFeature) => (
            <Feature
              key={firstFeature.name}
              feature={firstFeature}
              disableFiltering={disableFiltering}
            />
          ))}
        {featuresCount > FIRST_FEATURES_SIZE && (
          <Popover
            shown={shown && !isMobileView}
            showArrow
            appendTo="window"
            placement="top"
            zIndex={5000}
            fixed={false}
            minWidth={70}
            maxWidth={407}
            onClickOutside={() => setShown(false)}
            disableClickOutsideWhenClosed={true}
            moveBy={{ y: 6 }}
            onMouseEnter={() => {
              setShown(true);
            }}
            onMouseLeave={() => {
              setShown(false);
            }}
          >
            <Popover.Element>
              <PlusFeaturesTag featuresCount={featuresCount} />
            </Popover.Element>
            <Popover.Content>
              <Box
                padding="12px"
                display="flex"
                alignContent="middle"
                flexWrap="wrap"
                gap="6px"
              >
                <Box direction="vertical" gap="6px">
                  {Array.isArray(moreFeatures) &&
                    _.map(moreFeatures, (feature) => (
                      <Feature
                        key={feature.name}
                        feature={feature}
                        disableFiltering={disableFiltering}
                      />
                    ))}
                </Box>
              </Box>
            </Popover.Content>
          </Popover>
        )}
      </>
    </Box>
  );
};

const PlusFeaturesTag: FC<{ featuresCount: number }> = ({ featuresCount }) => {
  const {
    i18n: { language },
  } = useTranslation();

  return featuresCount > 2 ? (
    <Box marginLeft="6px" marginTop="2px" verticalAlign="middle">
      <TextButton underline="always" skin="dark" size="tiny">
        +{Intl.NumberFormat(language, {}).format(featuresCount - 2)}
      </TextButton>
    </Box>
  ) : (
    <></>
  );
};

const Feature: FC<{
  feature: AppName;
  disableFiltering?: boolean;
}> = ({ feature, disableFiltering }) => {
  const { t } = useTranslation();
  const { navigate, router } = useShallowRouter();
  const { name, isKey } = feature;
  const displayName = isKey
    ? t(`marketplace.templates-page.feature-${name}`)
    : name;

  const createUpdatedQueryParam = (selectedItem?: string): QueryParams => ({
    features: selectedItem,
  });

  const handleClick = (e: React.MouseEvent) => {
    if (disableFiltering) {
      return;
    }
    e.preventDefault();
    navigate(updateQueryParams(createUpdatedQueryParam(name), router));
  };

  return (
    <Box
      height="16px"
      backgroundColor="#eceff3"
      borderRadius="2px"
      padding="3px 6px"
      width="fit-content"
    >
      <Text size="tiny" weight="thin" onClick={handleClick}>
        {displayName}
      </Text>
    </Box>
  );
};

export default FeatureTags;
